import { toast } from "sonner";
import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { api, saveTokens, clearTokens, refreshSession } from "../lib/api";

const AuthContext = createContext(null);

const IDLE_MS = 15 * 60 * 1000;
const WARN_MS = 60 * 1000;
const ACTIVITY = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];
const lastActive = () => Number(localStorage.getItem("pf-last-active") || Date.now());
const touch = () => localStorage.setItem("pf-last-active", String(Date.now()));

// Warns at 14 min with no activity, signs out at 15 (shared across tabs via localStorage).
function useIdleLogout(user, logout) {
  const [left, setLeft] = useState(null);
  const warnRef = useRef(false);
  useEffect(() => {
    if (!user) return undefined;
    let last = 0;
    const mark = () => { const now = Date.now(); if (!warnRef.current && now - last > 5000) { last = now; touch(); } };
    const check = () => {
      const idle = Date.now() - lastActive();
      if (idle >= IDLE_MS) {
        warnRef.current = false;
        setLeft(null);
        localStorage.removeItem("pf-last-active");
        logout().then(() => toast("Logged out after 15 minutes with no activity. Your lists and view are saved."));
      } else if (idle >= IDLE_MS - WARN_MS) {
        warnRef.current = true;
        setLeft(Math.ceil((IDLE_MS - idle) / 1000));
      } else if (warnRef.current) {
        warnRef.current = false;
        setLeft(null);
      }
    };
    check();
    mark();
    ACTIVITY.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    const onVis = () => document.visibilityState === "visible" && check();
    document.addEventListener("visibilitychange", onVis);
    const t = setInterval(check, 1000);
    return () => { ACTIVITY.forEach((e) => window.removeEventListener(e, mark)); document.removeEventListener("visibilitychange", onVis); clearInterval(t); };
  }, [user, logout]);
  const stay = useCallback(() => { touch(); warnRef.current = false; setLeft(null); }, []);
  return { left, stay };
}

const StillThere = ({ left, stay, logout }) => (
  <Dialog open={left != null}>
    <DialogContent className="sm:max-w-sm [&>button]:hidden" onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} data-testid="idle-warning">
      <DialogHeader>
        <DialogTitle>Still there?</DialogTitle>
        <DialogDescription>You'll be logged out in <b data-testid="idle-countdown">{left}</b> second{left === 1 ? "" : "s"}. Your lists and view are saved.</DialogDescription>
      </DialogHeader>
      <div className="flex flex-col gap-2">
        <Button onClick={stay} className="h-12 rounded-full bg-blue-700 hover:bg-blue-800 text-base font-semibold" data-testid="idle-stay-button">Stay signed in</Button>
        <Button variant="ghost" onClick={logout} className="rounded-full text-red-600 hover:text-red-700" data-testid="idle-logout-button">Log out now</Button>
      </div>
    </DialogContent>
  </Dialog>
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => refreshSession().then((r) => setUser(r.data)).catch(() => { clearTokens(); setUser(false); }));
    const onLogout = () => setUser(false);
    window.addEventListener("auth:logout", onLogout);
    return () => window.removeEventListener("auth:logout", onLogout);
  }, []);

  const login = useCallback(async (pin) => {
    const { data } = await api.post("/auth/login", { pin });
    saveTokens(data);
    localStorage.setItem("pf-last-active", String(Date.now()));
    return data;
  }, []);

  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => {});
    clearTokens();
    localStorage.removeItem("pf-last-active");
    setUser(false);
  }, []);

  const idle = useIdleLogout(user, logout);

  const value = useMemo(() => ({ user, login, commit: setUser, logout }), [user, login, logout]);
  return (
    <AuthContext.Provider value={value}>
      {children}
      {user && <StillThere left={idle.left} stay={idle.stay} logout={logout} />}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
